import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './supabaseClient';
import { 
  Upload, Image, Check, X, AlertCircle, Search, 
  RefreshCw, Download, Trash2, FolderOpen, CheckSquare, Square
} from 'lucide-react';

const ProductImageManager = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('missing'); // 'all', 'missing', 'invalid'
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, currentFile: '' });
  const [results, setResults] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [skipExisting, setSkipExisting] = useState(true); // Skip products with existing images by default
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);

  const BUCKET_NAME = 'Foodline Product Images';
  const FOLDER_PATH = 'products';
  const PAGE_SIZE = 50;

  useEffect(() => {
    fetchProducts();
  }, [currentPage, filterType]);

  useEffect(() => {
    // Only filter by search locally since we're already filtering by type on server
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const filtered = products.filter(p => 
        p.name?.toLowerCase().includes(query) ||
        p.stock_code?.toLowerCase().includes(query)
      );
      setFilteredProducts(filtered);
    } else {
      setFilteredProducts(products);
    }
  }, [products, searchQuery]);

  // Fetch all products for stats (lightweight query)
  const fetchStats = async () => {
    try {
      // Get total count
      const { count: total } = await supabase
        .from('products')
        .select('*', { count: 'exact', head: true });

      // Get missing count (null or empty image)
      const { count: missing } = await supabase
        .from('products')
        .select('*', { count: 'exact', head: true })
        .or('image.is.null,image.eq.');

      // Get invalid count (has image but not valid supabase URL)
      const { data: allWithImages } = await supabase
        .from('products')
        .select('image')
        .not('image', 'is', null)
        .not('image', 'eq', '');

      const invalid = allWithImages?.filter(p => !isValidImageUrl(p.image)).length || 0;
      const valid = (total || 0) - (missing || 0) - invalid;

      return { total: total || 0, missing: missing || 0, invalid, valid };
    } catch (error) {
      console.error('Error fetching stats:', error);
      return { total: 0, missing: 0, invalid: 0, valid: 0 };
    }
  };

  const [stats, setStats] = useState({ total: 0, missing: 0, invalid: 0, valid: 0 });

  useEffect(() => {
    fetchStats().then(setStats);
  }, [products]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const from = (currentPage - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      let query = supabase
        .from('products')
        .select('id, stock_code, name, image, published', { count: 'exact' });

      // Apply filter based on filterType
      if (filterType === 'missing') {
        query = query.or('image.is.null,image.eq.');
      }
      // For 'invalid' and 'all', we fetch and filter client-side for invalid check

      const { data, error, count } = await query
        .order('stock_code')
        .range(from, to);

      if (error) throw error;

      let filteredData = data || [];
      
      // Client-side filter for invalid URLs (can't do regex in Supabase easily)
      if (filterType === 'invalid') {
        // For invalid, we need to fetch all with images and filter
        const { data: allData } = await supabase
          .from('products')
          .select('id, stock_code, name, image, published')
          .not('image', 'is', null)
          .not('image', 'eq', '')
          .order('stock_code');
        
        const invalidProducts = (allData || []).filter(p => !isValidImageUrl(p.image));
        const paginatedInvalid = invalidProducts.slice(from, to + 1);
        setProducts(paginatedInvalid);
        setTotalCount(invalidProducts.length);
        setLoading(false);
        return;
      }

      setProducts(filteredData);
      setTotalCount(count || 0);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const isValidImageUrl = (url) => {
    if (!url) return false;
    if (url.includes('foodline-admin-v3.netlify.app')) return false;
    if (url.includes('foodline-admin.netlify.app')) return false;
    if (url.includes('fooline-admin')) return false;
    if (!url.startsWith('https://dmnsynbxftuqmubdnhgw.supabase.co/')) return false;
    return true;
  };

  const getExpectedImageUrl = (stockCode) => {
    return `https://dmnsynbxftuqmubdnhgw.supabase.co/storage/v1/object/public/${BUCKET_NAME}/${FOLDER_PATH}/${stockCode}.jpg`;
  };

  // Handle single file upload with stock code input
  const handleSingleUpload = async (file, stockCode, skipIfExists = false) => {
    if (!stockCode) {
      return { success: false, error: 'No stock code provided' };
    }

    // Trim whitespace from stock code
    const cleanStockCode = stockCode.trim();

    try {
      // Check database directly for the product (not local state)
      const { data: product, error: lookupError } = await supabase
        .from('products')
        .select('id, stock_code, name, image')
        .eq('stock_code', cleanStockCode)
        .single();

      if (lookupError || !product) {
        return { success: false, stockCode: cleanStockCode, error: `Stock code "${cleanStockCode}" does not exist in database` };
      }

      // Check if product already has a valid image and skip if requested
      if (skipIfExists && product.image && isValidImageUrl(product.image)) {
        return { success: true, stockCode: cleanStockCode, name: product.name, skipped: true };
      }

      // Upload to bucket with stock code as filename
      const filePath = `${FOLDER_PATH}/${cleanStockCode}.jpg`;
      
      // Delete existing file if it exists (to replace)
      await supabase.storage.from(BUCKET_NAME).remove([filePath]);

      // Upload new file
      const { error: uploadError } = await supabase.storage
        .from(BUCKET_NAME)
        .upload(filePath, file, {
          contentType: file.type,
          upsert: true
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from(BUCKET_NAME)
        .getPublicUrl(filePath);

      // Update product with new image URL
      const { error: updateError } = await supabase
        .from('products')
        .update({ image: publicUrl })
        .eq('id', product.id);

      if (updateError) throw updateError;

      return { success: true, stockCode: cleanStockCode, name: product.name, url: publicUrl };
    } catch (error) {
      return { success: false, stockCode: cleanStockCode, error: error.message };
    }
  };

  // Handle bulk upload - files named by stock code
  const handleBulkUpload = async (files) => {
    setUploading(true);
    setUploadProgress({ current: 0, total: files.length, currentFile: '' });
    setResults([]);

    const uploadResults = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      // Extract stock code from filename
      // Handles formats like: "44996.jpg" or "44996 PRODUCT NAME.jpg"
      const fileName = file.name.split('/').pop().split('\\').pop();
      const nameWithoutExt = fileName.replace(/\.[^/.]+$/, '').trim();
      // Get stock code - either the whole name (if no spaces) or first part before space
      const stockCode = nameWithoutExt.includes(' ') 
        ? nameWithoutExt.split(' ')[0].trim()
        : nameWithoutExt;
      
      setUploadProgress({ 
        current: i + 1, 
        total: files.length, 
        currentFile: file.name 
      });

      const result = await handleSingleUpload(file, stockCode, skipExisting);
      uploadResults.push(result);

      // Small delay to prevent overwhelming the server
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    setResults(uploadResults);
    setUploading(false);
    fetchProducts(); // Refresh the list
    fetchStats().then(setStats); // Refresh stats
  };

  // Handle file selection
  const onFilesSelected = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      handleBulkUpload(files);
    }
  };

  // Manual single upload with stock code
  const [manualStockCode, setManualStockCode] = useState('');
  const [manualFile, setManualFile] = useState(null);

  const handleManualUpload = async () => {
    if (!manualFile || !manualStockCode) {
      alert('Please select a file and enter a stock code');
      return;
    }

    setUploading(true);
    const result = await handleSingleUpload(manualFile, manualStockCode);
    setResults([result]);
    setUploading(false);
    setManualStockCode('');
    setManualFile(null);
    fetchProducts();
  };

  // Fix invalid URLs by checking if image exists in bucket
  const fixInvalidUrls = async () => {
    // Fetch ALL products with invalid URLs (paginated)
    let allInvalidProducts = [];
    let page = 0;
    const batchSize = 1000;
    
    while (true) {
      const { data } = await supabase
        .from('products')
        .select('id, stock_code, image')
        .not('image', 'is', null)
        .not('image', 'eq', '')
        .range(page * batchSize, (page + 1) * batchSize - 1);
      
      if (!data || data.length === 0) break;
      
      const invalidInBatch = data.filter(p => !isValidImageUrl(p.image) && p.stock_code);
      allInvalidProducts = [...allInvalidProducts, ...invalidInBatch];
      
      if (data.length < batchSize) break;
      page++;
    }

    if (allInvalidProducts.length === 0) {
      alert('No invalid URLs to fix');
      return;
    }

    if (!window.confirm(`This will check ${allInvalidProducts.length} products for existing images in the bucket. Continue?`)) {
      return;
    }

    setUploading(true);
    setUploadProgress({ current: 0, total: allInvalidProducts.length, currentFile: 'Checking...' });
    const fixResults = [];

    for (let i = 0; i < allInvalidProducts.length; i++) {
      const product = allInvalidProducts[i];
      setUploadProgress({ 
        current: i + 1, 
        total: allInvalidProducts.length, 
        currentFile: product.stock_code 
      });

      const expectedUrl = getExpectedImageUrl(product.stock_code);
      
      try {
        // Check if file exists by trying to fetch it
        const response = await fetch(expectedUrl, { method: 'HEAD' });
        
        if (response.ok) {
          // File exists, update the product
          const { error } = await supabase
            .from('products')
            .update({ image: expectedUrl })
            .eq('id', product.id);

          if (error) throw error;
          fixResults.push({ success: true, stockCode: product.stock_code, url: expectedUrl });
        } else {
          fixResults.push({ success: false, stockCode: product.stock_code, error: 'Image not found in bucket' });
        }
      } catch (error) {
        fixResults.push({ success: false, stockCode: product.stock_code, error: error.message });
      }
    }

    setResults(fixResults);
    setUploading(false);
    fetchProducts();
    fetchStats().then(setStats);
  };

  // Select all visible products
  const handleSelectAll = () => {
    if (selectedProducts.size === filteredProducts.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(filteredProducts.map(p => p.id)));
    }
  };

  // Toggle individual selection
  const toggleSelection = (productId) => {
    const newSelected = new Set(selectedProducts);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelectedProducts(newSelected);
  };

  // Pagination
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setSelectedProducts(new Set());
  };

  const handleFilterChange = (newFilter) => {
    setFilterType(newFilter);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Product Image Manager</h1>
          <p className="text-gray-600 mt-1">Upload and manage product images by stock code</p>
        </div>
        <button
          onClick={fetchProducts}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
        >
          <RefreshCw size={18} />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Image size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Products</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Check size={20} className="text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Valid Images</p>
              <p className="text-2xl font-bold text-green-600">{stats.valid}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <AlertCircle size={20} className="text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Invalid URLs</p>
              <p className="text-2xl font-bold text-orange-600">{stats.invalid}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <X size={20} className="text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Missing Images</p>
              <p className="text-2xl font-bold text-red-600">{stats.missing}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Upload Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">Upload Images</h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Bulk Upload */}
          <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-green-500 transition-colors">
            <input
              type="file"
              ref={fileInputRef}
              onChange={onFilesSelected}
              accept="image/*"
              multiple
              className="hidden"
            />
            <FolderOpen size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="font-semibold text-gray-900 mb-2">Bulk Upload</h3>
            <p className="text-sm text-gray-600 mb-4">
              Select multiple images named by stock code<br />
              <span className="text-xs text-gray-500">(e.g., 10510.jpg or 10510 Product Name.jpg)</span>
            </p>
            
            {/* Skip duplicates checkbox */}
            <label className="flex items-center justify-center gap-2 mb-4 cursor-pointer">
              <input
                type="checkbox"
                checked={skipExisting}
                onChange={(e) => setSkipExisting(e.target.checked)}
                className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
              />
              <span className="text-sm text-gray-700">Skip products with existing images</span>
            </label>
            
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="px-6 py-2 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              Select Images
            </button>
          </div>

          {/* Single Upload */}
          <div className="border border-gray-200 rounded-xl p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Single Upload</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Stock Code</label>
                <input
                  type="text"
                  value={manualStockCode}
                  onChange={(e) => setManualStockCode(e.target.value)}
                  placeholder="Enter stock code"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Image File</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setManualFile(e.target.files?.[0] || null)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <button
                onClick={handleManualUpload}
                disabled={uploading || !manualFile || !manualStockCode}
                className="w-full px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                Upload
              </button>
            </div>
          </div>
        </div>

        {/* Fix Invalid URLs Button */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <button
            onClick={fixInvalidUrls}
            disabled={uploading || stats.invalid === 0}
            className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white font-semibold rounded-lg hover:bg-orange-700 disabled:opacity-50"
          >
            <RefreshCw size={18} />
            Fix {stats.invalid} Invalid URLs (Check Bucket)
          </button>
          <p className="text-xs text-gray-500 mt-2">
            This will check if images exist in the bucket and update product URLs accordingly
          </p>
        </div>
      </div>

      {/* Upload Progress */}
      {uploading && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="animate-spin">
              <RefreshCw size={20} className="text-blue-600" />
            </div>
            <span className="font-medium text-blue-900">
              Uploading {uploadProgress.current} of {uploadProgress.total}
            </span>
          </div>
          <p className="text-sm text-blue-700">Current: {uploadProgress.currentFile}</p>
          <div className="mt-2 bg-blue-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all"
              style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Results */}
      {results.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="font-bold text-gray-900 mb-4">
            Upload Results: {results.filter(r => r.success && !r.skipped).length} uploaded, {results.filter(r => r.skipped).length} skipped, {results.filter(r => !r.success).length} failed
          </h3>
          <div className="max-h-64 overflow-y-auto space-y-2">
            {results.map((result, index) => (
              <div 
                key={index}
                className={`flex items-center gap-3 p-3 rounded-lg ${
                  result.skipped ? 'bg-yellow-50' : result.success ? 'bg-green-50' : 'bg-red-50'
                }`}
              >
                {result.skipped ? (
                  <AlertCircle size={18} className="text-yellow-600 flex-shrink-0" />
                ) : result.success ? (
                  <Check size={18} className="text-green-600 flex-shrink-0" />
                ) : (
                  <X size={18} className="text-red-600 flex-shrink-0" />
                )}
                <span className="font-medium">{result.stockCode}</span>
                {result.skipped ? (
                  <span className="text-sm text-yellow-700 truncate">
                    Skipped - already has image ({result.name})
                  </span>
                ) : result.success ? (
                  <span className="text-sm text-green-700 truncate">
                    ✓ {result.name || 'Updated successfully'}
                  </span>
                ) : (
                  <span className="text-sm text-red-700">{result.error}</span>
                )}
              </div>
            ))}
          </div>
          <button
            onClick={() => setResults([])}
            className="mt-4 text-sm text-gray-600 hover:text-gray-900"
          >
            Clear Results
          </button>
        </div>
      )}

      {/* Filter & Search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-64">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name or stock code..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => handleFilterChange('all')}
              className={`px-4 py-2 rounded-lg font-medium ${
                filterType === 'all' 
                  ? 'bg-gray-900 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All ({stats.total})
            </button>
            <button
              onClick={() => handleFilterChange('missing')}
              className={`px-4 py-2 rounded-lg font-medium ${
                filterType === 'missing' 
                  ? 'bg-red-600 text-white' 
                  : 'bg-red-100 text-red-700 hover:bg-red-200'
              }`}
            >
              Missing ({stats.missing})
            </button>
            <button
              onClick={() => handleFilterChange('invalid')}
              className={`px-4 py-2 rounded-lg font-medium ${
                filterType === 'invalid' 
                  ? 'bg-orange-600 text-white' 
                  : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
              }`}
            >
              Invalid ({stats.invalid})
            </button>
          </div>
        </div>
      </div>

      {/* Products List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={handleSelectAll} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900">
              {selectedProducts.size === filteredProducts.length && filteredProducts.length > 0 ? (
                <CheckSquare size={18} className="text-green-600" />
              ) : (
                <Square size={18} />
              )}
              Select All
            </button>
            <span className="text-sm text-gray-500">
              Showing {filteredProducts.length} products
            </span>
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin mx-auto mb-4">
              <RefreshCw size={32} className="text-gray-400" />
            </div>
            <p className="text-gray-600">Loading products...</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="p-8 text-center">
            <Image size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-600">No products found</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 max-h-[500px] overflow-y-auto">
            {filteredProducts.map(product => (
              <div 
                key={product.id}
                className="flex items-center gap-4 p-4 hover:bg-gray-50"
              >
                <button onClick={() => toggleSelection(product.id)}>
                  {selectedProducts.has(product.id) ? (
                    <CheckSquare size={20} className="text-green-600" />
                  ) : (
                    <Square size={20} className="text-gray-400" />
                  )}
                </button>
                
                <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                  {product.image && isValidImageUrl(product.image) ? (
                    <img 
                      src={product.image} 
                      alt={product.name}
                      className="w-full h-full object-cover"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Image size={24} className="text-gray-300" />
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{product.name}</p>
                  <p className="text-sm text-gray-500">Stock Code: {product.stock_code || 'N/A'}</p>
                </div>

                <div className="flex-shrink-0">
                  {!product.image || product.image === '' ? (
                    <span className="px-3 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                      Missing
                    </span>
                  ) : !isValidImageUrl(product.image) ? (
                    <span className="px-3 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
                      Invalid URL
                    </span>
                  ) : (
                    <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                      Valid
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="p-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {((currentPage - 1) * PAGE_SIZE) + 1} - {Math.min(currentPage * PAGE_SIZE, totalCount)} of {totalCount}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className="px-3 py-1 rounded border border-gray-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                First
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 rounded border border-gray-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              
              <div className="flex items-center gap-1">
                {/* Show page numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 py-1 rounded text-sm ${
                        currentPage === pageNum
                          ? 'bg-green-600 text-white'
                          : 'border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 rounded border border-gray-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 rounded border border-gray-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductImageManager;
